import Product = FBInstant.Product;
import DEG_TO_RAD = PIXI.DEG_TO_RAD;
import Container = PIXI.Container;
import { TweenMax } from 'gsap';
import { getGameSize } from '../../../Config/Levels';
import { ShopCatalog, ShopProductIDs } from '../../../Config/Shop';
import Core, { GameOrientation } from '../../../Core/Core';
import getLogger, { ILogger } from '../../../Core/Logger';
import { HudActions, HudEmitter } from '../../../Managers/Hud/HudEmitter';
import { Popup } from '../../../Managers/Popups';
import { createOutlineText, scaleToFit } from '../../../Utils/Utils';
import HudView from '../../../View/HudView';
import { DynamicGrid } from '../../Shared/Layout/DynamicGrid';
import { ScreenBlur } from '../../Shared/ScreenBlur';
import { ScrollView } from '../../Shared/ScrollView/ScrollView';
import { PopupHeader } from '../PopupHeader';
import { FreeGift } from './Products/FreeGift';

const logger: ILogger = getLogger('[SHOP-POPUP]');

export class ShopPopup extends Container {
    private scrollView: ScrollView;
    private grid: DynamicGrid;
    private header: PopupHeader;
    private bg: ScreenBlur;
    private giftIndex: number = -1;

    constructor(hudView: HudView, catalog: Product[]) {
        super();
        const vSpacing = Core.orientation === GameOrientation.PORTRAIT ? 5 : 15;
        const grid = (this.grid = new DynamicGrid(Core.orientation, 0, 7, vSpacing));
        this.setCatalog(catalog);
        grid.x += grid.width / 2;
        this.bg = new ScreenBlur();
        this.bg.interactive = true;

        this.header = new PopupHeader('shop', Popup.SHOP, 0.8);

        const containerWidth = grid.width;
        const containerHeight = grid.height;

        // const bgTexture
        const popupBg = new PIXI.mesh.NineSlicePlane(
            PIXI.utils.TextureCache['commonui/popup-bg'],
            80,
            80,
            80,
            80
        );

        popupBg.width = containerWidth * 1.1;
        popupBg.height =
            Core.orientation === GameOrientation.PORTRAIT
                ? containerHeight * 1.13
                : containerHeight * 1.2;

        const container = new Container();
        popupBg.x = getGameSize().gameWidth / 2 - popupBg.width / 2;
        popupBg.y = getGameSize().gameHeight / 2 - popupBg.height / 2 + this.header.height * 0.2;
        container.x = getGameSize().gameWidth / 2 - grid.width / 2;
        container.y = getGameSize().gameHeight / 2 - grid.height / 2;

        this.header.y = Math.max(popupBg.y - this.header.height * 0.08, this.header.height * 0.3);

        this.scrollView = new ScrollView(grid.width + 10, popupBg.height, container, true);
        this.scrollView.addItem(grid);
        this.addChild(this.bg, popupBg, container, this.header);
    }

    public tweenIn() {
        this.header.tweenIn();
    }

    public tweenOut() {
        this.header.tweenOut();
        TweenMax.to(this, 0.3, {
            alpha: 0,
            onComplete: () => {
                this.destroy();
            },
        });
    }

    public showFreeGift() {
        const giftElement = this.grid.elements[this.giftIndex] as FreeGift;
        giftElement.showGift();
        const giftContents = ShopCatalog[ShopProductIDs.FreeGift].items[0];
        const { id, val } = giftContents;
        const giftReward = [id, val];
        HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.BOTREWARD, 1, [giftReward]);
    }

    public setCatalog(catalog: Product[]) {
        this.grid.clear();
        this.giftIndex = -1;
        const uiElements = [];
        const labels = [];
        const keys = Object.keys(ShopCatalog);
        catalog.sort((a, b) => keys.indexOf(a.productID) - keys.indexOf(b.productID));
        for (const product of catalog) {
            if (product.productID === ShopProductIDs.FreeGift) {
                this.giftIndex = catalog.indexOf(product);
            }
            const item = ShopCatalog[product.productID as ShopProductIDs];
            if (!item) {
                logger.warn(`Product id '${product.productID}' is not defined at ShopCatalog.`);
                continue;
            }
            const ui = Object.create(item.uiClass.prototype);
            ui.constructor.bind(ui)(product);
            uiElements.push(ui);
            if (item.label) {
                const labelId = item.label;
                const label = new PIXI.Sprite(
                    PIXI.utils.TextureCache[`commonui/shop-label-${labelId}`]
                );
                label.anchor.set(0.5);
                label.scale.set(0.9);
                const labelText = createOutlineText(
                    Core.local.getText(`label${labelId}`),
                    new PIXI.Rectangle(0, 0, label.width * 0.9, label.height * 0.9),
                    'Baloo'
                );
                labelText.x += label.width * 0.05;
                labelText.y -= label.height * 0.05;
                labelText.rotation = 35 * DEG_TO_RAD;
                label.addChild(labelText);
                const newScale = Math.max(
                    Math.min(
                        scaleToFit(
                            labelText.width,
                            labelText.height,
                            label.width * 0.6,
                            label.height * 0.8
                        ),
                        0.8
                    ),
                    0.5
                );
                labelText.scale.set(newScale * (1 + 1 - label.scale.x));
                ui.addChild(label);
                label.x = ui.width * 0.5 - label.width * 0.3;
                label.y = -ui.height * 0.5 + label.height * 0.2;
                labels.push(label);
            }
        }
        this.grid.addItem(...uiElements);
        labels.forEach(x => {
            x.position = this.grid.toLocal(x.position, x.parent);
            this.grid.addChild(x);
        });
    }
}
